import { alpha, createTheme, lighten, ThemeOptions } from '@mui/material/styles';

/**
 * Mind Australia MUI Theme
 * Generated from: Mind Australia Brand Style Guide Ver 1.0
 *
 * COLOUR (source: guide pages 34-41)
 * - Jacaranda #923AFF — primary brand colour, digital use only (also spot colour on print).
 * - Iris      #8245B2 — secondary option for Jacaranda.
 * - Inky      #4512D6
 * - Indigo    #382180
 * - Ochre     #DEAB31
 * - Clay      #B6671B
 * - Sand      #FFF7FF
 * Accessibility rules from the guide (pages 53-55): only white text on a brand
 * colour, or brand-colour text on white, is approved — no other colour-on-colour
 * text combinations. This theme's contrastText values follow that rule.
 *
 * TYPOGRAPHY (source: guide pages 44-50)
 * - Raleway (Google Fonts) — primary display typeface, used for headings.
 * - Roboto  (Google Fonts) — secondary typeface, used for body copy and whenever
 *   Raleway falls below accessible minimum sizes (16px / 12pt).
 * - Juniper Bay — decorative support typeface. Uppercase only, 16px/12pt minimum,
 *   not accessible on mobile/social, and NOT to be used for headings or body text.
 *   Not wired into MUI typography variants; use sparingly as a manual override
 *   (see `mindTypefaces.juniperBay` export) only with marketing approval.
 *
 * Load the webfonts in your app (e.g. via @fontsource/raleway and
 * @fontsource/roboto, or a <link> tag to Google Fonts) — this file only
 * references the family names.
 */

// ---------------------------------------------------------------------------
// Brand colour palette
// ---------------------------------------------------------------------------
export const mindColors = {
	jacaranda: '#923AFF',
	iris: '#8245B2',
	inky: '#4512D6',
	indigo: '#382180',
	ochre: '#DEAB31',
	clay: '#B6671B',
	sand: '#FFF7FF',
	white: '#FFFFFF',
} as const;

export const mindTypefaces = {
	primary: 'Raleway, sans-serif',
	secondary: 'Roboto, sans-serif',
	// Decorative only — see restrictions in the header comment above.
	juniperBay: '"Juniper Bay", sans-serif',
} as const;

// Augment MUI's palette so `theme.palette.mind.*` is typed and usable in sx props.
declare module '@mui/material/styles' {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Palette {
		mind: typeof mindColors;
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface PaletteOptions {
		mind?: typeof mindColors;
	}
}

const themeOptions: ThemeOptions = {
	palette: {
		mode: 'light',
		mind: mindColors,
		primary: {
			// Jacaranda is the core brand colour, for digital-only use.
			main: mindColors.jacaranda,
			dark: mindColors.iris,
			contrastText: mindColors.white, // white-on-Jacaranda passes AA/AAA per the guide.
		},
		secondary: {
			main: mindColors.ochre,
			dark: mindColors.clay,
			contrastText: mindColors.white,
		},
		background: {
			default: mindColors.sand,
			paper: mindColors.white,
		},
		text: {
			primary: mindColors.jacaranda,
			secondary: mindColors.inky,
		},
		info: {
			main: lighten(mindColors.inky, .4),
			contrastText: mindColors.white,
		},
	},

	typography: {
		fontFamily: mindTypefaces.secondary, // Roboto as the base/body font.
		h1: { fontFamily: mindTypefaces.primary, fontWeight: 700 },
		h2: { fontFamily: mindTypefaces.primary, fontWeight: 700 },
		h3: { fontFamily: mindTypefaces.primary, fontWeight: 600 },
		h4: { fontFamily: mindTypefaces.primary, fontWeight: 600 },
		h5: { fontFamily: mindTypefaces.primary, fontWeight: 600 },
		h6: { fontFamily: mindTypefaces.primary, fontWeight: 600 },
		subtitle1: { fontFamily: mindTypefaces.primary, fontWeight: 500 },
		subtitle2: { fontFamily: mindTypefaces.primary, fontWeight: 500 },
		// Body/caption/overline stay on Roboto per the guide's accessibility guidance.
		body1: { fontFamily: mindTypefaces.secondary, fontSize: '1rem' }, // 16px min.
		body2: { fontFamily: mindTypefaces.secondary },
		button: { fontFamily: mindTypefaces.primary, fontWeight: 600, textTransform: 'none' },
		caption: { fontFamily: mindTypefaces.secondary },
		overline: { fontFamily: mindTypefaces.secondary },
	},

	shape: {
		// The logo's recurring "pill" motif (guide pages 211-260) is echoed in
		// rounded UI elements; buttons go fully pill-shaped below.
		borderRadius: 12,
	},

	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 999, // pill shape
					paddingLeft: 24,
					paddingRight: 24,
				},
				sizeLarge: {
					minWidth: 400,
				},
			},
		},
		MuiCard: {
			variants: [
				{
					props: { variant: 'outlined' },
					style: {
						color: mindColors.iris,
						backgroundColor: alpha(mindColors.clay, 0.05),
						padding: 8,
					},
				},
			],
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 999,
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: `
				body {
					color: ${mindColors.jacaranda};
          			background-color: ${mindColors.sand};
        		}
      		`,
		},
	},
};

export const MindTheme = createTheme(themeOptions);

